import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Printer, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const fmt = (iso) =>
  new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/New_York" });

/** Admin batch print: one black & white page per participant with meetings. */
export default function MatchmakingPrint() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/admin/matchmaking/print-data")
      .then(({ data }) => setData(data))
      .catch(() => setErr("Admin sign-in required."));
  }, []);

  return (
    <div className="min-h-screen bg-white text-black" data-testid="mm-print-page">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .itinerary-page { break-after: page; }
          body { background: #fff; }
        }
      `}</style>

      <div className="no-print border-b border-zinc-200 px-6 py-4 flex items-center justify-between sticky top-0 bg-white z-10">
        <Link to="/admin/matchmaking" className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-black">
          <ArrowLeft size={14} /> Back to Console
        </Link>
        <div className="text-sm text-zinc-500">{data ? `${data.participants.length} itineraries — one page each` : "Loading…"}</div>
        <button onClick={() => window.print()} className="inline-flex items-center gap-2 bg-black text-white px-5 h-10 text-xs uppercase tracking-[0.18em]" data-testid="mm-print-all-btn">
          <Printer size={14} /> Print All / Save PDF
        </button>
      </div>

      {err && <p className="p-8 text-sm">{err}</p>}
      {data && data.participants.length === 0 && (
        <p className="p-8 text-sm text-zinc-500">No published meetings yet — publish the matchmaking schedule (or add T+L sourcing meetings) first.</p>
      )}

      {data && data.participants.map((p, idx) => (
        <div key={idx} className="itinerary-page max-w-[7.5in] mx-auto px-8 py-10" data-testid={`mm-print-person-${idx}`}>
          <div className="border-b-2 border-black pb-4">
            <div className="text-[10px] uppercase tracking-[0.3em]">Business Matchmaking Itinerary</div>
            <h1 style={{ fontFamily: "Georgia, serif" }} className="text-2xl mt-2">{data.event.title}</h1>
            <div className="text-sm mt-1">{data.event.date} · Travel + Leisure Headquarters, Orlando</div>
          </div>
          <div className="mt-4 flex items-baseline justify-between">
            <div>
              <div className="text-lg font-semibold">{p.company || p.name}</div>
              <div className="text-sm">{p.name && p.company ? `${p.name} · ` : ""}{p.role}{p.extra ? ` · ${p.extra}` : ""}</div>
            </div>
            <div className="text-xs text-right">Matchmaking window · {data.event.matchmaking_window}</div>
          </div>
          <table className="w-full mt-6 text-sm border-collapse">
            <thead>
              <tr className="border-b border-black text-left">
                <th className="py-2 pr-3 font-semibold w-[1.7in]">Time (ET)</th>
                <th className="py-2 pr-3 font-semibold">Meeting With</th>
                <th className="py-2 pr-3 font-semibold w-[2in]">Location</th>
                <th className="py-2 font-semibold w-[1.1in]">Type</th>
              </tr>
            </thead>
            <tbody>
              {p.meetings.map((m, i) => (
                <tr key={i} className="border-b border-zinc-300 align-top">
                  <td className="py-2 pr-3 tabular-nums">{m.start_iso ? `${fmt(m.start_iso)} – ${fmt(m.end_iso)}` : "—"}</td>
                  <td className="py-2 pr-3 font-medium">{m.with}</td>
                  <td className="py-2 pr-3">{m.location}</td>
                  <td className="py-2 text-xs uppercase tracking-wide">{m.kind}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-10 pt-3 border-t border-zinc-300 text-[11px] leading-relaxed">
            Keep this schedule available when you arrive. Check in at the Business Matchmaking Check-In Table inside the Supplier Showcase at least five minutes before your meeting ·
            Free parking at the 501 W. Church St. Garage · Questions: info@displayamerica.com
          </div>
        </div>
      ))}
    </div>
  );
}
